import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Injector,
  Signal,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest, map } from 'rxjs';
import {
  KI_SPERRTEXT_KEYS,
  KI_STATUS_GRUENDE,
  KiButtonState,
  KiFunktion,
  KiSperrgrund,
  KiStatusZustand,
} from '../../../models/ki.model';
import { KiStatusService } from '../../../services/ki-status.service';
import { KiMarkComponent } from '../ki-mark/ki-mark.component';

let naechsteHinweisNr = 0;

interface KiButtonZustand {
  state: KiButtonState;
  sperrgrund?: KiSperrgrund;
}

/** Sperre einer Funktion laut KI-Status; `null`, wenn sie nutzbar ist. Während der Status lädt, ohne Grund. */
export function kiStatusSperre(
  zustand: KiStatusZustand,
  funktion: KiFunktion,
): { state: 'gesperrt'; sperrgrund?: KiSperrgrund } | null {
  if (zustand.art === 'laedt') return { state: 'gesperrt' };
  if (zustand.art === 'fehler') return { state: 'gesperrt', sperrgrund: 'status-fehler' };
  const eintrag = zustand.status.funktionen?.[funktion];
  if (!eintrag) return { state: 'gesperrt', sperrgrund: 'status-fehler' };
  if (eintrag.aktiv) return null;
  const grund = eintrag.grund && KI_STATUS_GRUENDE.includes(eintrag.grund) ? eintrag.grund : undefined;
  return { state: 'gesperrt', sperrgrund: grund };
}

/**
 * Schaltfläche für jede KI-Aktion (page-ki.jsx:54-75). Gesperrt bleibt sie fokussierbar
 * (aria-disabled statt disabled), damit der Klick den Grund im Hinweis erklären kann.
 * Mit `funktion` kommt der Zustand aus `GET …/ki/status` des Projekts der Route.
 */
@Component({
  selector: 'app-ki-button',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [KiMarkComponent, MatTooltip, RouterLink, TranslatePipe],
  templateUrl: './ki-button.component.html',
  styleUrl: './ki-button.component.scss',
})
export class KiButtonComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly route = inject(ActivatedRoute);
  private readonly injector = inject(Injector);
  private readonly knopf = viewChild.required<ElementRef<HTMLButtonElement>>('knopf');

  readonly label = input.required<string>();
  readonly state = input<KiButtonState>('aktiv');
  readonly sperrgrund = input<KiSperrgrund>();
  readonly funktion = input<KiFunktion>();
  readonly ausloesen = output<void>();

  protected readonly hinweisId = `ki-button-hinweis-${++naechsteHinweisNr}`;

  private readonly projektId = toSignal(
    combineLatest(this.route.pathFromRoot.map((step) => step.paramMap)).pipe(
      map((paramMaps) => paramMaps.reduce<string | null>((found, params) => params.get('projectId') ?? found, null)),
    ),
    { initialValue: null },
  );

  /**
   * Hängt nur an Projekt und Funktion, nicht am Status selbst: sonst löste jeder Fehler über
   * `status()` sofort den nächsten Versuch aus. Neu versucht wird erst mit neuer Instanz oder neuem Projekt.
   */
  private readonly projektStatus = computed<Signal<KiStatusZustand> | null>(() => {
    const projectId = this.projektId();
    if (!this.funktion() || !projectId) return null;
    // Erst hier geholt: ohne `funktion` braucht der Button weder den Service noch HttpClient.
    return untracked(() => this.injector.get(KiStatusService).status(projectId));
  });

  /** `laeuft` des Aufrufers geht vor; sein `gesperrt` (etwa „ungespeichert“) gilt nur bei aktivem Status. */
  protected readonly zustand = computed<KiButtonZustand>(() => {
    const eigener: KiButtonZustand = { state: this.state(), sperrgrund: this.sperrgrund() };
    const funktion = this.funktion();
    if (!funktion || eigener.state === 'laeuft') return eigener;
    return this.sperreAusStatus(funktion) ?? eigener;
  });

  /** Ohne Sperrgrund gibt es nichts zu erklären: weder Hinweis noch Tooltip. */
  protected readonly sperrtextKey = computed(() => {
    const { state, sperrgrund } = this.zustand();
    return state === 'gesperrt' && sperrgrund ? KI_SPERRTEXT_KEYS[sperrgrund] : null;
  });
  /** Jeder Wechsel von Zustand oder Grund schließt den Hinweis — er erklärte den alten Grund. */
  private readonly offen = linkedSignal({ source: this.sperrtextKey, computation: () => false });
  protected readonly hinweisOffen = computed(() => this.offen() && this.sperrtextKey() !== null);

  /** Ziel „Projekt bearbeiten“ nur beim Grund „verbindung“ und nur, wenn die Route ein Projekt kennt. */
  protected verbindungEinrichtenLink(): string[] | null {
    if (this.zustand().sperrgrund !== 'verbindung') return null;
    const projectId = this.projektId();
    return projectId ? ['/projects', projectId, 'edit'] : null;
  }

  /** Für Aufrufer, deren Dialog den Fokus beim Schließen auf diese Schaltfläche zurückgeben soll. */
  fokussieren(): void {
    this.knopf().nativeElement.focus();
  }

  aktivieren(): void {
    const { state } = this.zustand();
    if (state === 'gesperrt') {
      if (this.sperrtextKey()) this.offen.update((offen) => !offen);
      return;
    }
    if (state === 'aktiv') this.ausloesen.emit();
  }

  @HostListener('document:click', ['$event'])
  schliessenBeiKlickDaneben(event: MouseEvent): void {
    if (this.offen() && !this.host.nativeElement.contains(event.target as Node)) {
      this.offen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  schliessenBeiEscape(): void {
    this.offen.set(false);
  }

  private sperreAusStatus(funktion: KiFunktion): KiButtonZustand | null {
    const status = this.projektStatus();
    if (!status) return { state: 'gesperrt', sperrgrund: 'status-fehler' };
    return kiStatusSperre(status(), funktion);
  }
}
